/**
 * Third-person player body (Steve, 64x64 wide skin + overlay layers + armor
 * layers) and the vanilla inventory preview.
 *
 *   const pm = new PlayerModel(env); scene.add(pm.group);
 *   pm.update(player.poseView(alpha), alpha);
 *   pm.rightHand  // ItemInHandLayer frame: parent held-item meshes here (see below)
 *
 * `rightHand` is vanilla's frame right after
 *   arm.translateAndRotate; rotateX(-90); rotateY(180); translate(-1/16, 1/8, -5/8)
 * in block units, handedness flip included, so apply the item's vanilla
 * `thirdperson_righthand` display transform (extras.applyHandheld does the
 * handheld/bow ones) to a mesh whose sprite plane faces +Z and is centred on
 * its origin (extras.itemSpriteMesh).
 */

import * as THREE from 'three';
import { itemNameOf } from '../../game/itemIds';
import type { PlayerPoseView } from '../../game/types';
import type { EnvUniforms } from '../env';
import { buildPart, entityMaterial, type EntityMat, fallbackEnv, loadInto, modelRoot, setEffects, sheetTexture, wrapPi } from './core';
import { FireOverlay } from './extras';
import { Humanoid } from './humanoid';
import { isSpearId } from '../../game/spear';
import { buildShield, lightShield } from '../shield';

const PI = Math.PI;


export function armorMaterialOf(id: number): string {
  const n = itemNameOf(id);
  if (!n) return '';
  const m = /^(LEATHER|IRON|GOLDEN|DIAMOND|CHAINMAIL|NETHERITE)_/.exec(n);
  if (!m) return '';
  return m[1] === 'GOLDEN' ? 'gold' : m[1].toLowerCase();
}

/** Legacy 64x32 skins -> 64x64 (copy right limbs mirrored into the left slots). */
function modernizeSkin(img: HTMLImageElement): CanvasImageSource {
  if (img.naturalHeight !== 32) return img;
  const c = document.createElement('canvas');
  c.width = 64; c.height = 64;
  const g = c.getContext('2d')!;
  g.drawImage(img, 0, 0);
  // mirror each face of limb (sx,sy) -> (dx,dy): faces laid out as top/bottom/right/front/left/back
  const limb = (sx: number, sy: number, dx: number, dy: number): void => {
    const faces: Array<[number, number, number, number, number, number]> = [
      [4, 0, 4, 4, 8, 0], [8, 0, 4, 4, 4, 0], // top, bottom
      [0, 4, 4, 12, 8, 4], [4, 4, 4, 12, 4, 4], [8, 4, 4, 12, 0, 4], [12, 4, 4, 12, 12, 4],
    ];
    for (const [fx, fy, w, h, tx, ty] of faces) {
      g.save();
      g.translate(dx + tx + w, dy + ty);
      g.scale(-1, 1);
      g.drawImage(c, sx + fx, sy + fy, w, h, 0, 0, w, h);
      g.restore();
    }
  };
  limb(0, 16, 16, 48);
  limb(40, 16, 32, 48);
  return c;
}

export class PlayerModel {
  readonly group = new THREE.Group();
  readonly rightHand: THREE.Object3D;
  readonly leftHand: THREE.Object3D;
  /** alias of rightHand */
  readonly heldSlot: THREE.Object3D;
  private readonly h: Humanoid;
  private readonly swim = new THREE.Group();
  private readonly lean = new THREE.Group();
  private readonly fire: FireOverlay;
  private armorIds = '';
  private shield: THREE.Group | null = null;
  private shieldOn = false;
  private blocking = false;
  private readonly shieldEnv: EnvUniforms;
  private readonly modelOpts: { flat?: boolean; srgbOut?: boolean };
  // elytra wings (vanilla ElytraModel): built on first use, pivots in body-pixel space
  private wings: { root: THREE.Group; l: THREE.Group; r: THREE.Group; mat: EntityMat; broken: boolean } | null = null;
  private wingX = 0.2617994;
  private wingZ = -0.2617994;

  constructor(env: EnvUniforms = fallbackEnv(), opts: { flat?: boolean; srgbOut?: boolean } = {}) {
    this.h = new Humanoid(env, { sheet: 'entity/player/wide/steve', modern: true, tw: 64, th: 64 }, opts.flat, opts.srgbOut);
    const root = modelRoot();
    root.add(this.h.root);
    this.lean.add(root);
    this.swim.add(this.lean);
    this.group.add(this.swim);
    this.fire = new FireOverlay(env, 0.6, 1.8, opts.srgbOut);
    this.group.add(this.fire.group);
    this.rightHand = this.heldSlot = this.h.rightHand;
    this.leftHand = this.h.leftHand;
    this.shieldEnv = env;
    this.modelOpts = opts;
  }

  /** Strap a shield to the left forearm; `blocking` raises the arm in front of the body. */
  setShield(has: boolean, blocking: boolean): void {
    this.blocking = has && blocking;
    if (has === this.shieldOn) return;
    this.shieldOn = has;
    if (has && !this.shield) {
      const g = buildShield(this.shieldEnv, 'world');
      // the arm frame is in pixels (the model root divides by 16); the plate sits just in front of the forearm
      g.scale.setScalar(16);
      g.position.set(1, 7, -2);
      this.shield = g;
    }
    if (this.shield) {
      if (has) this.h.leftArm.add(this.shield);
      else this.shield.removeFromParent();
    }
  }

  /**
   * Blocking raises the forearm across the chest; the shield counter-turns so its face stays
   * upright and forward (vanilla's shield_blocking display carries the same ~45 deg compensation).
   */
  private poseShield(blocking: boolean): void {
    const g = this.shield;
    if (!g) return;
    if (!blocking) { g.quaternion.identity(); return; }
    g.quaternion.copy(this.h.leftArm.quaternion).invert();
    g.quaternion.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.15, -0.25, 0)));
  }

  setSkin(url: string): void {
    const t = new THREE.Texture();
    t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter;
    t.generateMipmaps = false; t.flipY = false; t.colorSpace = THREE.SRGBColorSpace;
    loadInto(t, url, modernizeSkin);
    this.h.setSkin(t);
  }

  /** Vanilla ElytraModel.setupAnim: folded at rest, spread while gliding (pitch by vertical speed), smoothed. */
  private poseWings(p: PlayerPoseView): void {
    if (!p.elytra) { if (this.wings) this.wings.root.visible = false; return; }
    if (!this.wings) {
      const mat = entityMaterial(this.shieldEnv, sheetTexture('entity/equipment/wings/elytra'), { flat: this.modelOpts.flat, srgbOut: this.modelOpts.srgbOut });
      this.h.mats.push(mat);
      const cube = (mirror: boolean) => ({ tex: [22, 0] as const, from: [mirror ? 0 : -10, 0, 0] as const, size: [10, 20, 2] as const, inflate: 1, mirror });
      const l = buildPart({ cubes: [cube(false)] }, 64, 32, mat);
      const r = buildPart({ cubes: [cube(true)] }, 64, 32, mat);
      l.position.set(5, 0, 0); r.position.set(-5, 0, 0);
      const root = new THREE.Group();
      root.position.set(0, 0, 2);
      root.add(l, r);
      this.h.body.add(root);
      this.wings = { root, l, r, mat, broken: false };
    }
    const w = this.wings;
    w.root.visible = true;
    let tx = 0.2617994, tz = -0.2617994, ty = 0;
    if (p.gliding) {
      // factor 1 = fully open; a steep dive (vy -> -1 of speed) folds them back toward rest
      const sp = Math.hypot(p.x - p.px, p.y - p.py, p.z - p.pz) || 1;
      const vy = p.vy ?? 0;
      const f = vy < 0 ? 1 - Math.pow(Math.min(1, -vy / Math.max(sp, 0.08)), 1.5) : 1;
      tx = f * 0.34906585 + (1 - f) * 0.2617994;
      tz = f * (-PI / 2) + (1 - f) * -0.2617994;
    } else if (p.sneaking) { tx = 0.6981317; tz = -0.7853982; ty = 3; }
    this.wingX += (tx - this.wingX) * 0.25;
    this.wingZ += (tz - this.wingZ) * 0.25;
    w.l.rotation.set(this.wingX, 0, this.wingZ);
    w.r.rotation.set(this.wingX, 0, -this.wingZ);
    w.l.position.y = w.r.position.y = ty;
  }

  setVisible(b: boolean): void { this.group.visible = b; }

  update(p: PlayerPoseView, alpha: number, timeTicks = performance.now() / 50): void {
    const g = this.group;
    g.position.set(p.px + (p.x - p.px) * alpha, p.py + (p.y - p.py) * alpha, p.pz + (p.z - p.pz) * alpha);
    g.rotation.y = p.yaw;
    const key = p.armor.join(',');
    if (key !== this.armorIds) {
      this.armorIds = key;
      this.h.setArmor(p.armor.map(armorMaterialOf));
    }
    this.poseWings(p);
    const headY = -wrapPi(p.headYaw - p.yaw);
    const headX = -p.pitch;
    const sw = p.swimming;
    this.h.animate({
      limbSwing: p.limbSwing, limbAmt: Math.min(1, p.limbSwingAmount), headY, headX, age: timeTicks,
      attack: p.swing,
      zombieArms: false, bow: p.usingBow && !p.useAnim, holding: p.heldItem !== 0 && !p.usingBow && !p.useAnim,
      holdingLeft: this.shieldOn, blockingLeft: this.shieldOn && (this.blocking || p.sneaking),
      useAnim: p.useAnim ?? '', spear: isSpearId(p.heldItem),
      sneaking: p.sneaking && !sw && !p.sitting && !p.sleeping, swimming: sw && !p.sitting && !p.sleeping, bob: false, sitting: !!p.sitting,
    });
    // vanilla lowers the crouching body slightly; swimming lies the body flat about the hips
    this.swim.position.set(0, 0, 0); this.swim.rotation.set(0, 0, 0); this.lean.position.set(0, 0, 0);
    if (p.sneaking && !sw) this.swim.position.y = -0.125;
    if (sw) {
      this.swim.position.y = 0.3;
      this.swim.rotation.x = -PI / 2 + p.pitch;
      this.lean.position.y = -0.75;
    }
    if (p.sleeping) {
      // lying on the back along the bed: feet toward `yaw`, centred on the mattress
      this.swim.rotation.x = PI / 2; this.swim.position.set(0, 0.2, -0.9); this.lean.position.set(0, 0, 0);
    }
    this.poseShield(this.shieldOn && (this.blocking || p.sneaking));
    if (this.shield) lightShield(this.shield, Math.max(p.light, 0.12));
    setEffects(this.h.mats, p.light, p.hurtTime > 0, 0, p.x !== undefined ? { x: p.x, y: p.y + 1, z: p.z } : undefined);
    this.fire.update(p.onFire, performance.now() / 1000);
  }

  dispose(): void { for (const m of this.h.mats) m.dispose(); this.fire.dispose(); }
}

/**
 * Vanilla InventoryScreen.renderEntityInInventory: scale 30 px/block, feet at
 * box (25,67) of the 49x70 preview box, body/head turn toward the mouse.
 * `lookX/lookY` = mouse offset in GUI px from the model's pivot (feet-centre x,
 * eye height): lookX = mouseX - 51, lookY = mouseY - 25 in vanilla screen
 * coordinates (box origin (26,8): feet at (51,75)). Output is drawn into
 * `target` scaled to its size (assumed the 49x70 box at any integer scale).
 */
export class PlayerPreview {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene = new THREE.Scene();
  private cam = new THREE.OrthographicCamera(-1, 1, 1, -1, -10, 10);
  private model: PlayerModel | null = null;

  private ensure(): boolean {
    if (this.renderer) return true;
    try {
      const r = new THREE.WebGLRenderer({ alpha: true, antialias: false, premultipliedAlpha: true, preserveDrawingBuffer: true });
      r.setClearColor(0x000000, 0);
      this.renderer = r;
    } catch { return false; }
    this.model = new PlayerModel(fallbackEnv(), { flat: true, srgbOut: true });
    this.scene.add(this.model.group);
    return true;
  }

  render(target: HTMLCanvasElement, lookX: number, lookY: number, pose: Partial<PlayerPoseView> = {}): void {
    if (!this.ensure() || !this.renderer || !this.model) return;
    const W = target.width, H = target.height;
    const r = this.renderer;
    if (r.domElement.width !== W || r.domElement.height !== H) r.setSize(W, H, false);
    const f = Math.atan(-lookX / 40), gg = Math.atan(-lookY / 40);
    // vanilla: body yaw 180+f*20, head 180+f*40, pitch -g*20 (degrees); front is toward the viewer
    const toRad = PI / 180;
    const yaw = PI - f * 20 * toRad;
    const p: PlayerPoseView = {
      x: 0, y: 0, z: 0, px: 0, py: 0, pz: 0,
      yaw, headYaw: PI - f * 40 * toRad, pitch: gg * 20 * toRad,
      limbSwing: 0, limbSwingAmount: 0, swing: 0, sneaking: false, sprinting: false, swimming: false, flying: false,
      usingBow: false, hurtTime: 0, heldItem: 0, armor: [0, 0, 0, 0], onFire: false, light: 1,
      ...pose,
    };
    this.model.update(p, 1);
    // vanilla also pitches the whole figure (xRot g*20deg about the pivot at feet+..): tilt around the hips
    this.model.group.rotation.x = 0;
    // box 49x70 at scale 30: feet at (24.5.., 67) from top
    const ppb = 30 * (H / 70);
    const hw = W / ppb / 2;
    const top = 67 / 70 * H / ppb; // world height of the top edge above the feet
    this.cam.left = -hw; this.cam.right = hw; this.cam.top = top; this.cam.bottom = top - H / ppb;
    this.cam.updateProjectionMatrix();
    this.cam.position.set(0, 0, 5);
    this.cam.lookAt(0, 0, 0);
    r.render(this.scene, this.cam);
    const ctx = target.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(r.domElement, 0, 0);
  }

  dispose(): void {
    this.model?.dispose();
    this.renderer?.dispose();
    this.renderer = null; this.model = null;
  }
}

void sheetTexture;
